/**
 * How far a running total is from its target, as a sentence.
 *
 * Extracted from `TargetsSummaryBar` so the pinned mini strip on a phone and the full grid it
 * stands in for cannot disagree. Two places computing "is this on target?" from the same numbers is
 * two places to check when they say different things — and the rule below is subtle enough that a
 * second implementation would get it wrong.
 *
 * ── two rules live here, and they measure different objects ──
 *
 * `targetStatus` / `ON_TARGET` judge ONE MACRO that is still being worked toward: the builder is
 * counting down to a finished menu, so below the target the cell keeps counting however small the
 * gap, and only an overshoot past 2% is worth turning red.
 *
 * `dayBand` / `DAY_ON_TARGET` judge A WHOLE DAY that is finished and can no longer be changed. It is
 * symmetric — a day well under target is as much a finding as one over — and much looser, because at
 * month scale a 2% band paints thirty calendar cells in four colours and the grid stops reading as
 * "where are the problems".
 *
 * They are together in this file so that difference stays deliberate rather than becoming drift, and
 * so nothing has to re-derive either one. Nothing should ever apply both to the same number.
 */

import { GRAM, KCAL, num } from "../ui/format";
import {
  CALORIES,
  POINTS,
  POINTS_SHORT,
  pointsFromKcal,
  pointsFromMacro,
  type NutritionDisplayMode,
  type PointsMacroKey,
} from "../ui/nutritionUnits";

/**
 * How far PAST a target still reads as being on it, rather than as a חריגה.
 *
 * 1g over an 80g target is not an overshoot worth turning a cell red, so a small overrun stays
 * quiet. It governs the OVER direction only.
 *
 * It used to work in both directions, and that was the bug: 78.7 of 80 fell inside the ±2% band
 * and the cell printed "היעד הושלם" directly above two numbers that plainly disagreed. Below the
 * target the cell now keeps counting down, however small the gap — which is what a countdown is.
 */
export const ON_TARGET = 0.02;

const f1 = (n: number) => Math.round(n * 10) / 10;

export type TargetStatus = {
  /** past the target by more than the quiet band */
  over: boolean;
  /** reached: `gap <= 0` and not over */
  hit: boolean;
  /** how much is still missing; negative once past the target */
  gap: number;
  /** 0–∞, unclamped — the caller decides where to cut the bar off */
  pct: number;
};

export const targetStatus = (current: number, target: number): TargetStatus => {
  const gap = target - current;
  const over = target > 0 && gap < -target * ON_TARGET;
  // Reached means REACHED: `gap <= 0`, not "close enough". Everything from exactly on target up to
  // the over threshold (80.5 of 80) reads as done rather than as an overshoot; anything short of
  // it, by any amount, keeps counting down.
  const hit = target > 0 && gap <= 0 && !over;
  return { over, hit, gap, pct: target > 0 ? Math.round((current / target) * 100) : 0 };
};

/** Which figure a target is: the energy total, a macro, or fibre (which never converts). */
export type TargetMetric = "calories" | PointsMacroKey | "fiber";

/**
 * A target figure in the unit the screen prints it in — grams and kcal unchanged, or points.
 *
 * In points mode the countdown is judged on THESE numbers rather than on the kcal and grams under
 * them, so "היעד הושלם" can never sit above 23.6 / 23.7: the same rule as `ON_TARGET`'s own bug,
 * one rounding step later.
 */
export const inDisplayUnit = (n: number, metric: TargetMetric, mode: NutritionDisplayMode = CALORIES): number => {
  if (mode !== POINTS || metric === "fiber") return n;
  return metric === "calories" ? pointsFromKcal(n) : pointsFromMacro(metric, n);
};

/**
 * `1,234.5` — a number that is ALREADY points, printed as `nutritionUnits` prints one. Its own
 * formatter is not exported, and the cells here format points they have already rounded.
 */
export const pointsNum = (n: number): string =>
  (Math.round(n * 10) / 10).toLocaleString("he-IL", { maximumFractionDigits: 1 });

/**
 * The distance as words.
 *
 * `unit: ""` means calories, which are whole and grouped ("2,373"); grams keep a decimal. `isLog`
 * switches the sentence from a countdown ("נותרו 640") to a report ("640 מתחת ליעד"), because the
 * food log says what a trainee ATE while the builder counts down to a finished menu.
 *
 * In points mode `current` and `target` are still kcal / grams; `metric` says which macro they are
 * (calories when `unit` is ""), and the distance is said in points. Fibre stays in grams.
 */
export const remainingLabel = (
  current: number,
  target: number,
  {
    unit = ` ${GRAM}`,
    isLog = false,
    mode = CALORIES,
    metric,
  }: { unit?: string; isLog?: boolean; mode?: NutritionDisplayMode; metric?: TargetMetric } = {}
): string => {
  const resolved: TargetMetric | undefined = metric ?? (unit === "" ? "calories" : undefined);
  const inPoints = mode === POINTS && resolved !== undefined && resolved !== "fiber";
  const c = inPoints ? inDisplayUnit(current, resolved, mode) : current;
  const t = inPoints ? inDisplayUnit(target, resolved, mode) : target;

  const { over, hit, gap } = targetStatus(c, t);
  const unitLabel = inPoints ? ` ${POINTS_SHORT}` : unit === "" ? ` ${KCAL}` : unit;
  const amount = (n: number) => (inPoints ? pointsNum(n) : unit === "" ? num(n) : f1(n));

  if (over) return `חריגה של ${amount(-gap)}${unitLabel}`;
  if (hit) return isLog ? "על היעד" : "היעד הושלם";
  return isLog ? `${amount(gap)}${unitLabel} מתחת ליעד` : `נותרו ${amount(gap)}${unitLabel}`;
};

/* ── a finished day, for the food log's month calendar ─────────────────────────────────────── */

/** Inside ±7% of the day's target, a DAY still reads as on it. See the header for why not 2%. */
export const DAY_ON_TARGET = 7;

/** Past +20% an overshoot stops being "a bit over" and becomes a חריגה worth a red cell. */
export const DAY_FAR_OVER = 20;

export type DayBand = "ok" | "warn" | "over" | "under" | "none";

/**
 * A day's distance from its target, as a signed whole percent. `null` when there is no target —
 * a day with no goal is not "0% of nothing".
 */
export const dayDeviationPct = (value: number, target: number): number | null =>
  target > 0 ? Math.round((value / target - 1) * 100) : null;

/**
 * Which band a whole day falls in.
 *
 * Derived from the ROUNDED percentage rather than the raw ratio, and that is the important part: the
 * calendar cell prints `dayDeviationPct` right next to the tint this returns, so reading them off
 * different numbers would eventually paint an amber cell under a label saying `+7%`. Going through
 * one integer makes that disagreement unrepresentable — the same failure `ON_TARGET`'s own comment
 * records, where a cell said "היעד הושלם" above two numbers that plainly did not agree.
 */
export const dayBand = (value: number, target: number): DayBand => {
  const dev = dayDeviationPct(value, target);
  if (dev === null) return "none";
  if (dev > DAY_FAR_OVER) return "over";
  if (dev > DAY_ON_TARGET) return "warn";
  if (dev < -DAY_ON_TARGET) return "under";
  return "ok";
};
